import api from "./index";

// Notification API endpoints
export const getNotifications = async (filters = {}) => {
  try {
    const response = await api.get("/notifications/messages/", {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await api.patch(`/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await api.patch("/notifications/mark-all-read");
    return response.data;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

export const deleteNotification = async (notificationId) => {
  try {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

export const deleteMultipleNotifications = async (notificationIds) => {
  try {
    const response = await api.delete("/notifications/bulk-delete", {
      data: { notificationIds },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting multiple notifications:", error);
    throw error;
  }
};

export const getNotificationStats = async () => {
  try {
    const response = await api.get("/notifications/stats");
    return response.data;
  } catch (error) {
    console.error("Error fetching notification stats:", error);
    throw error;
  }
};

// agencyWide mirrors the Off / On Market Pipeline board's own scope switch,
// which lives in the browser - without it the badge would count one board and
// the page would draw another (Christian Gravias, Monday 12944444309).
export const getPipelineCounts = async ({
  agencyWide,
  enquiriesAgencyWide,
  enquiriesAgents,
} = {}) => {
  const params = {};
  if (agencyWide !== undefined) params.agency_wide = agencyWide;
  // Buyer Enquiries & Offers carries its own switch under its own names - it
  // is a separate surface from the appraisal boards and the two are routinely
  // set differently.
  if (enquiriesAgencyWide !== undefined) params.enquiries_agency_wide = enquiriesAgencyWide;
  if (enquiriesAgents && enquiriesAgents.length) {
    params.enquiries_agents = enquiriesAgents.join(",");
  }
  const response = await api.get("/notifications/pipeline-counts/", {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

// Real-time notification subscription
export const subscribeToNotifications = (userId, onNotificationReceived) => {
  // This would typically use WebSocket or Server-Sent Events
  // For now, we'll simulate with polling
  const interval = setInterval(async () => {
    try {
      const response = await api.get("/notifications/realtime", {
        params: { userId, lastCheck: new Date().toISOString() },
      });

      if (response.data.newNotifications.length > 0) {
        response.data.newNotifications.forEach((notification) => {
          onNotificationReceived(notification);
        });
      }
    } catch (error) {
      console.error("Error checking for new notifications:", error);
    }
  }, 30000); // Check every 30 seconds

  return () => clearInterval(interval);
};

// Notification preferences
export const getNotificationPreferences = async () => {
  try {
    const response = await api.get("/notifications/preferences");
    return response.data;
  } catch (error) {
    console.error("Error fetching notification preferences:", error);
    throw error;
  }
};

export const updateNotificationPreferences = async (preferences) => {
  try {
    const response = await api.put("/notifications/preferences", preferences);
    return response.data;
  } catch (error) {
    console.error("Error updating notification preferences:", error);
    throw error;
  }
};

// Notification actions
export const handleNotificationAction = async (
  notificationId,
  action,
  actionData = {}
) => {
  try {
    const response = await api.post(
      `/notifications/${notificationId}/actions`,
      {
        action,
        actionData,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error handling notification action:", error);
    throw error;
  }
};

// Lead-specific notification actions
export const handleLeadNotification = async (notificationId, leadId) => {
  return handleNotificationAction(notificationId, "view_lead", { leadId });
};

export const handleCallbackNotification = async (
  notificationId,
  callbackData
) => {
  return handleNotificationAction(
    notificationId,
    "schedule_callback",
    callbackData
  );
};

export const handleHandoffNotification = async (notificationId, callId) => {
  return handleNotificationAction(notificationId, "accept_handoff", { callId });
};

export const handleEscalationNotification = async (
  notificationId,
  escalationData
) => {
  return handleNotificationAction(
    notificationId,
    "handle_escalation",
    escalationData
  );
};

// Mock data for development (remove in production)
export const getMockNotifications = () => {
  return [
    {
      id: 1,
      type: "hot_lead_assigned",
      title: "Hot Lead Assigned - Sarah Johnson",
      message:
        "New hot lead assigned for property at 123 Collins Street. Contact: +61 4XX XXX XXX. Lead scored 95% based on AI analysis.",
      timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
      read: false,
      priority: "high",
      data: {
        leadId: "L001",
        contactName: "Sarah Johnson",
        phone: "+61 4XX XXX XXX",
        email: "sarah.j@email.com",
        propertyAddress: "123 Collins Street, Melbourne VIC 3000",
        leadScore: 95,
        aiSummary:
          "Highly interested in 3-bedroom properties, ready to buy within 30 days",
        transcript:
          "Customer expressed immediate interest in viewing properties...",
      },
    },
    {
      id: 2,
      type: "warm_lead_assigned",
      title: "Warm Lead Assigned - Michael Chen",
      message:
        "Warm lead assigned for rental inquiry. Contact: +61 4XX XXX XXX. Lead scored 75%.",
      timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
      read: false,
      priority: "high",
      data: {
        leadId: "L002",
        contactName: "Michael Chen",
        phone: "+61 4XX XXX XXX",
        email: "michael.c@email.com",
        propertyType: "Rental",
        leadScore: 75,
        aiSummary: "Looking for 2-bedroom rental, budget $800-1200/week",
      },
    },
    {
      id: 3,
      type: "callback_reminder",
      title: "Callback Reminder - Emma Wilson",
      message:
        "Scheduled callback with Emma Wilson in 20 minutes. Property: 456 Bourke Street. Prepare for follow-up discussion.",
      timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      read: true,
      priority: "medium",
      data: {
        contactName: "Emma Wilson",
        phone: "+61 4XX XXX XXX",
        scheduledTime: new Date(Date.now() + 20 * 60 * 1000).toISOString(),
        propertyAddress: "456 Bourke Street, Melbourne VIC 3000",
        notes: "Interested in viewing this weekend, has pre-approval",
      },
    },
    {
      id: 4,
      type: "handoff_request",
      title: "Live Handoff Request - David Brown",
      message:
        "Customer David Brown requests human agent during AI call. Currently on hold. Urgent response needed.",
      timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
      read: false,
      priority: "high",
      data: {
        contactName: "David Brown",
        phone: "+61 4XX XXX XXX",
        callId: "CALL_001",
        waitTime: "2 minutes",
        reason: "Complex property questions, prefers human assistance",
      },
    },
    {
      id: 5,
      type: "escalation",
      title: "Lead Escalation - Lisa Anderson",
      message:
        "Hot lead Lisa Anderson has been unattended for 1 hour. Escalating to admin for immediate action.",
      timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      read: false,
      priority: "high",
      data: {
        leadId: "L003",
        contactName: "Lisa Anderson",
        phone: "+61 4XX XXX XXX",
        assignedAgent: "John Smith",
        escalationReason: "No response within 1 hour",
        originalAssignment: new Date(
          Date.now() - 120 * 60 * 1000
        ).toISOString(),
      },
    },
    {
      id: 6,
      type: "daily_summary",
      title: "Daily Summary - January 15, 2024",
      message:
        "Today's performance: 8 new leads, 5 follow-ups completed, 3 hot leads pending, 85% response rate.",
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      read: true,
      priority: "medium",
      data: {
        newLeads: 8,
        completedFollowUps: 5,
        pendingHotLeads: 3,
        responseRate: 85,
        totalCalls: 12,
        successfulCalls: 10,
      },
    },
    {
      id: 7,
      type: "system_alert",
      title: "System Alert - AI Dialer Service Issue",
      message:
        "AI dialer service experiencing high latency. Call success rate dropped to 60%. Technical team investigating.",
      timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      read: false,
      priority: "high",
      data: {
        service: "AI Dialer",
        issue: "High latency",
        impact: "Call success rate dropped to 60%",
        status: "Investigating",
        affectedCalls: 15,
      },
    },
    {
      id: 8,
      type: "ai_call_completed",
      title: "AI Call Completed - Robert Taylor",
      message:
        "AI call with Robert Taylor completed successfully. Lead qualified as warm. Follow-up scheduled for tomorrow.",
      timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
      read: true,
      priority: "medium",
      data: {
        contactName: "Robert Taylor",
        phone: "+61 4XX XXX XXX",
        callDuration: "3 minutes 45 seconds",
        outcome: "Lead qualified as warm",
        nextAction: "Follow-up scheduled for tomorrow",
        transcript: "AI: Hello, this is Collings AI assistant...",
      },
    },
  ];
};

export default {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteMultipleNotifications,
  getNotificationStats,
  subscribeToNotifications,
  getNotificationPreferences,
  updateNotificationPreferences,
  handleNotificationAction,
  handleLeadNotification,
  handleCallbackNotification,
  handleHandoffNotification,
  handleEscalationNotification,
  getMockNotifications,
};

// Staff tool: unsubscribe a whole profile (email and/or mobile, both channels)
// even when they aren't a findable contact.
export const suppressEmail = (email, note = "", phone = "") =>
  api.post("/notifications/email/suppress/", { email, note, phone }).then((r) => r.data);

export const checkEmailSuppressed = (email) =>
  api.get("/notifications/email/suppress/", { params: { email } }).then((r) => r.data);
